// A readable stand-in for a raw URL.
//
// Document links pasted into this app are typically SharePoint/OneDrive URLs —
// 300+ characters of percent-encoded path plus a `?id=...&viewid=...` query.
// Printed literally they wrap over three lines and drown the title they sit
// under, so the UI shows this short form and keeps the full URL on the anchor's
// href and title (hover) instead.
//
// Returns e.g. "indorama0.sharepoint.com/…/AllItems.aspx" — host first, because
// the host is the part a reader actually uses to recognise where a link goes.
export function prettyUrl(raw, maxLen = 60) {
  const value = String(raw || "").trim();
  if (!value) return "";

  let parsed;
  try {
    // A link stored without a scheme ("indorama0.sharepoint.com/...") isn't a
    // valid URL to the parser, so assume https rather than bailing out.
    parsed = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    // Not parseable as a URL at all — show it as-is, just capped in length.
    return value.length > maxLen ? `${value.slice(0, maxLen - 1)}…` : value;
  }

  const host = parsed.hostname.replace(/^www\./, "");

  let path;
  try {
    path = decodeURIComponent(parsed.pathname);
  } catch {
    // Malformed percent-escapes (a stray "%" in a filename) throw here.
    path = parsed.pathname;
  }

  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return host;

  const full = `${host}/${segments.join("/")}`;
  if (full.length <= maxLen) return full;

  // Too long: keep the two ends a reader needs — where it lives, and what it
  // is — and elide the middle.
  const last = segments[segments.length - 1];
  const elided = `${host}/…/${last}`;
  if (elided.length <= maxLen) return elided;

  // Even the final segment is oversized (a long generated filename).
  return `${host}/…/${last.slice(0, Math.max(1, maxLen - host.length - 4))}…`;
}
